import { Session, useInitialReferrer } from '@aspendental/shared-components-web';
import { getSessionId } from '@aspendental/shared-utils/utils';
import * as Sentry from '@sentry/nextjs';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import React, { useEffect } from 'react';
import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { Layout } from '@src/components/templates/layout';
import { getLogger } from '@src/lib/logging/logger';
import '@aspendental/shared-components-web/lib/dynamic-form.css';
import '@aspendental/shared-components-web/lib/fonts.css';
import '@aspendental/shared-components-web/lib/global.css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
import { toggleSentry, isSentryEnabled } from '@src/services/sentry';
import { initializeSentryClient } from '@src/pages/utils/sentryClientInit';
export function reportWebVitals({
  id,
  name,
  label,
  value
}: NextWebVitalsMetric) {
  const logger = getLogger('client');
  logger.info({
    name,
    category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
    value: Math.round(name === 'CLS' ? value * 1000 : value),
    // values must be integers
    id // id unique to current page load
  });
}
type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};
const App = ({
  Component,
  pageProps
}: AppPropsWithLayout) => {
  const router = useRouter();
  const config = pageProps?.config as BrandThemeConfig;
  const {
    features,
    featureFlags
  } = config ?? {};
  const brandName = config?.name || 'Aspen Dental';
  const brazeEnabled = features?.analytics?.braze?.enabled;
  const brazeSdkKey = features?.analytics?.braze?.sdkKey;
  const brazeBaseUrl = features?.analytics?.braze?.baseUrl;
  const brazeLoggingEnabled = features?.analytics?.braze?.enableLogging;
  const environment = pageProps?.env?.ENVIRONMENT_NAME || 'prod';
  toggleSentry(!!pageProps?.config?.features?.sentry?.enabled);

  // regular page and blog page
  const disableStickyFooterOnMobile = pageProps?.page?.disableStickyFooterOnMobile || pageProps?.pageData?.disableStickyFooterOnMobile;
  // regular page and blog page
  const disableStickyFooterOnDesktop = pageProps?.page?.disableStickyFooterOnDesktop || pageProps?.pageData?.disableStickyFooterOnDesktop;
  const disableHeaderTopAlertBanner = pageProps?.page?.disableHeaderTopAlertBanner || pageProps?.pageData?.disableHeaderTopAlertBanner;
  const disableHeaderBottomAlertBanner = pageProps?.page?.disableHeaderBottomAlertBanner || pageProps?.pageData?.disableHeaderBottomAlertBanner;
  useEffect(() => {
    if (brazeEnabled) {
      import('@src/pages/utils/braze-exports').then(({
        initialize,
        openSession,
        automaticallyShowInAppMessages
      }) => {
        initialize(brazeSdkKey, {
          baseUrl: brazeBaseUrl,
          enableLogging: brazeLoggingEnabled,
          allowUserSuppliedJavascript: true
        });
        automaticallyShowInAppMessages();
        openSession();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brazeEnabled]);

  // get  set referrer for marketing payload.
  useInitialReferrer();
  useEffect(() => {
    if (isSentryEnabled()) {
      initializeSentryClient(environment);
      Sentry.setTag('brand', brandName);
      Sentry.setContext('user', {
        sessionId: getSessionId(),
        brand: brandName
      });
    }
  }, [brandName, environment]);
  useEffect(() => {
    // when component is mounting we remove server side rendered css:
    const jssStyles = document.querySelector('#jss-server-side');
    if (jssStyles && jssStyles.parentNode) {
      jssStyles.parentNode.removeChild(jssStyles);
    }
    router.events.on('routeChangeComplete', () => null);
    return () => {
      router.events.off('routeChangeComplete', () => null);
    };
  }, [router.events]);
  const getLayout = Component.getLayout ?? (page => {
    return <Layout locale={router.locale || 'en-US'} preview={pageProps.preview} appData={pageProps.appData} isFooterLite={features.footer.lite} isFooterAlt={features.footer.alt} withSimpleLayout={features.footer.withSimpleLayout} footerData={pageProps.footerData} showHeaderNavMenu={featureFlags?.showHeaderNavigationMenu || false} breadcrumbsData={pageProps.breadcrumbsData} withFixedHeader={!features.header.hideHeaderOnScroll} disableStickyFooterOnMobile={disableStickyFooterOnMobile} disableStickyFooterOnDesktop={disableStickyFooterOnDesktop} hideLocation={featureFlags?.hideHeaderFacilityLocator ?? false} hidePrimaryActionButton={featureFlags?.hideHeaderSchedulingButton ?? pageProps.page?.hideScheduleButtonInHeader ?? false} disableHeaderTopAlertBanner={disableHeaderTopAlertBanner} disableHeaderBottomAlertBanner={disableHeaderBottomAlertBanner}>
					{page}
				</Layout>;
  });
  const dentrinoClientId = brandName === 'ClearChoice' ? pageProps?.env?.CC_DENTRINO_CLIENT_ID : pageProps?.env?.DENTRINO_CLIENT_ID || '';
  const dentrinoClientSecret = brandName === 'ClearChoice' ? pageProps?.env?.CC_DENTRINO_CLIENT_SECRET : pageProps?.env?.DENTRINO_CLIENT_SECRET || '';

  // check to see if there is a config if there is no config the app cannot load.
  // if there is no config try to do a reload to get the config from the server
  // limit reloads to 1 time
  if (!config) {
    const hasPageReloaded = globalThis?.sessionStorage?.getItem('hasPageReloaded');
    if (!hasPageReloaded) {
      globalThis?.sessionStorage?.setItem('hasPageReloaded', '1');
      globalThis.location.reload();
    }
    return <div>
				{' '}
				This page could not be loaded <a href="/">Return to Home</a>
			</div>;
  }
  globalThis?.sessionStorage?.removeItem('hasPageReloaded');
  return <Session data-sentry-element="Session" data-sentry-component="App" data-sentry-source-file="_app.page.tsx">
			<ContextProviders config={pageProps.config} preview={pageProps.preview} environment={environment} user={pageProps.user} pageTheme={pageProps?.page?.pageTheme} defaultToCdn={pageProps?.config?.featureFlags?.defaultToCdn} dentrinoClientId={dentrinoClientId} dentrinoClientSecret={dentrinoClientSecret} deviceType={pageProps.deviceType} hideHeaderFacilityLocator={featureFlags?.hideHeaderFacilityLocator} stateCodeFromHeaders={pageProps.stateCodeFromHeaders} data-sentry-element="ContextProviders" data-sentry-source-file="_app.page.tsx">
				{getLayout(<Component {...pageProps} />)}
			</ContextProviders>
		</Session>;
};
export default appWithTranslation(App);